import React from 'react';
import { Box, Stack, Grid, useMediaQuery, useTheme } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { UpgradeModal } from '../common/LockIcon';
import { useGetSlateInfoQuery, useGetSlates } from '../common/queries/slateQueries';
import moment from 'moment';
import { APIConstants } from '../utils/api/constants';
import UnauthenticatedView from '../common/UnauthenticatedView';
import { SettingsLoader } from '../common/SettingsLoader';
import NoSlatesView from '../common/NoSlatesView';
import { SelectInput, lineupBuilderPoolSizeOptions, percentToFirstOptions } from '../common/selectOptions';
import { setPercentChange, setPoolSize } from './store/actions';
import { filterSlates } from '../common/helpers/SelectorHelpers';
import { useQueryParams } from '../utils/queryParams';
import { setSite } from '../common/store/actions';
import { userSubscribed } from '../utils/authentication';
import SLSSettingsSelectors from './SLSSettingsSelectors';

const Settings = () => {
  const { selectedSite, selectedSlate, selectedSport, selectedType, authenticated, } = useSelector((state: any) => state.common);
  const { poolSize, percentToFirst } = useSelector((state: any) => state.lineupBuilder);
  const [openModal, setOpenModal] = React.useState(false);
  const [disableUpdateInfo, setDisableUpdateInfo] = React.useState(false);
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { data: updateInfo } = useGetSlateInfoQuery(selectedSlate?.slateId?.toString(), disableUpdateInfo || selectedSlate?.slateId === undefined)
  const { data, isLoading: slatesLoading } = useGetSlates('SINGLELINEUPSIM', false);
  const slates = data?.slates;
  let queryParams = useQueryParams();
  const isUserSubscribed = userSubscribed(authenticated, selectedSite, selectedSport);
  const queryParamsSite = queryParams.get('site') || 'dk';

  const setSize = (size: number) => {
    dispatch(setPoolSize(size));
  }

  const handlePercentChange = (percent: number) => {
    dispatch(setPercentChange(percent));
  }

  React.useEffect(() => {
    if (updateInfo && selectedSlate?.slateId?.toString()) {
      setDisableUpdateInfo(true);
    }
    else {
      setDisableUpdateInfo(false);
    }
  }, [updateInfo]);

  React.useEffect(() => {
    if (selectedSlate?.slateId?.toString()) {
      setDisableUpdateInfo(false);
    }
  }, [selectedSlate]);

  React.useEffect(() => {
    if (queryParamsSite && queryParamsSite?.toLowerCase() !== selectedSite?.site?.toLowerCase()) {
      dispatch(setSite({site: queryParamsSite}));
    }
  }, [queryParamsSite])

  // Mobile: always show chip selectors (handles loading/no-slate states internally)
  if (isMobile) {
    return (
      <Grid container sx={{
        backgroundColor: 'transparent',
        width: '100%',
      }}>
        <SLSSettingsSelectors />
      </Grid>
    );
  }

  if (authenticated.status === APIConstants.STARTED || slatesLoading) {
    return (
      <SettingsLoader />
    )
  }

  if (!isUserSubscribed) {
    return (
      <UnauthenticatedView
        title='Contest Settings'
        app='SINGLELINEUPSIM'
        backgroundColor='rgba(161, 0, 18, 0.15)'
      />
    )
  }

  if (!slatesLoading && (!slates || (filterSlates(slates, selectedSport, selectedSite, selectedType).length === 0))) {
    return (
      <NoSlatesView title='Contest Settings' app='SINGLELINEUPSIM' backgroundColor='rgba(161, 0, 18, 0.15)' />
    )
  }

  // Desktop: original layout
  return (
    <Grid container sx={{
      backgroundColor: 'rgba(199, 0, 22, 0.15)',
      width: '100%',
      position: 'relative',
      padding: { xs: '10px', lg: '7px 20px' },
      '& .hide': {
        display: 'none',
      },
      '& .MuiSlider-markLabel': {
        fontSize: '10px',
      }
    }}>
      <UpgradeModal open={openModal} onClose={() => setOpenModal(false)} />
      <Grid container>
        <Grid item xs={12} container sx={{
          fontSize: 20,
          fontWeight: 700,
          fontFamily: 'Inter',
        }}>
          <Stack direction='row' flexWrap='wrap' alignItems='center' justifyContent='space-between' width='100%'>
            <Stack direction='row' flexWrap='wrap' alignItems='top' height='100%' width={{ xs: '100%', sm: 'auto' }}>
              <SLSSettingsSelectors />
            </Stack>
            <Stack direction='row' flexWrap='wrap' alignItems='top' height='100%' flexGrow={1} flexShrink={1}>
              <SelectInput
                data={lineupBuilderPoolSizeOptions}
                label="Pool Size"
                value={poolSize}
                onSelect={(selection: any) => setSize(selection.value)}
                tierLock={false}
                bottomLabel={'Small Contest is for contests with less than 1000 entries, large contest is for anything greater than 1000 entries.'}
              />
              <SelectInput
                data={percentToFirstOptions}
                label="Percent to First"
                value={percentToFirst}
                onSelect={(selection: any) => handlePercentChange(selection.value)}
                tierLock={false}
              />
            </Stack>
            <Stack direction='column' rowGap={1} alignItems='end' height={'100%'} sx={{ display: { xs: 'none', xl: 'block' } }}>
              <Stack ml={2}>
                {updateInfo?.projectionsLastUpdated && <Box fontSize={10} color={'#BCBCBC'} paddingRight={2} textAlign={'end'}>Projections Last Updated: {moment.utc(updateInfo?.projectionsLastUpdated).local().format('M/D h:mm A')}</Box>}
                {updateInfo?.ownershipLastUpdated && <Box fontSize={10} color={'#BCBCBC'} paddingRight={2} textAlign={'end'}>Ownership Last Updated: {moment.utc(updateInfo?.ownershipLastUpdated).local().format('M/D h:mm A')}</Box>}
              </Stack>
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default Settings